/**
 * Why a message looks forged.
 *
 * Not a spam filter — a spam filter guesses, and a guess that fires on real
 * mail teaches you to click past it. Everything here is EVIDENCE: a verdict
 * your own mail server already reached, or a construct in the message that
 * legitimate mail does not contain. Each finding names what was found and what
 * it means, so the reader can check it rather than trust a score.
 *
 * Written 2026-08-25 after two phishes landed looking clean in the reader
 * (Bob: "this is an attack — could you do a better job of identifying it as
 * bogus?"). Both carried all of the following, and mailx surfaced NONE of it:
 *
 *   X-Spam-Flag: YES                        <- his own server, score 6.7/4.5
 *   Authentication-Results: ... spf=fail
 *   From: bobip@bobf.frankston.com          <- his own address, forged
 *   To:   bobip@bobf.frankston.com
 *   Return-Path: <3EDTkWNvbY@visionsurveysqld.com.au>
 *   <area coords="0,0,10000,10000" href="http://.../redirect.php?newurl=...">
 *
 * No header in that list was read anywhere in the codebase, and the one
 * impersonation check that existed (displayNameClaimsOtherAddress) needs a
 * display name, which these messages did not have. The whole visible card was
 * one hidden link through an open redirector, with the victim's own address
 * base64'd into the fragment so the landing page could prefill it.
 *
 * Deliberately NOT included: bare SPF failure on mail that isn't claiming to
 * be you. Forwarded mail fails SPF constantly, and flagging that would be
 * exactly the cry-wolf banner this is trying not to be.
 */

/** One piece of evidence that a message is not what it claims to be. */
export interface TrustFinding {
    /** Stable identifier — for tests, logs, and any future suppression UI. */
    id: "server-spam-verdict" | "self-spoof" | "relay-auth-mismatch"
        | "zero-width-obfuscation" | "hidden-link-overlay" | "redirector-link";
    /** `danger`: near-certain forgery. `caution`: worth a second look.
     *  `info`: something the server said, in a register that is not an
     *  accusation — a bulk-mail verdict on a sender DMARC proved. */
    severity: "danger" | "caution" | "info";
    /** One line, naming what was found. Never "this message is suspicious". */
    text: string;
    /** The specific evidence — a header value, a host, a score. */
    detail: string;
}

export interface TrustInput {
    /** Every header line, in order, as mailparser's `headerLines`. Repeated
     *  headers matter: Authentication-Results and Received both appear more
     *  than once and the interesting one is not always the first. */
    headerLines: { key: string; line: string }[];
    /** Envelope From address. */
    fromAddress: string;
    /** Addresses belonging to the reader — their own aliases. Used only to
     *  recognise a message claiming to BE them. */
    ownAddresses: string[];
    /** Sanitized HTML body, for the link-construct checks. */
    bodyHtml: string;
    /** The reader has approved this sender or its domain in allowlist.jsonc —
     *  "Always: *@icecer.com" in the remote-content banner, or "Trust all
     *  senders at ‹domain›" in the address menu. It answers the only question
     *  a spam score cannot: is this mail WANTED. It never silences forgery
     *  evidence, and it counts only on a message whose sender was actually
     *  proved — otherwise the list would be a switch a forger could flip by
     *  typing a trusted address into From. */
    senderTrusted?: boolean;
    /** Plain-text body, for the character-level checks. Separate from the HTML
     *  because markup legitimately contains things prose does not, and the
     *  zero-width test would trip over an entity or an attribute value. */
    bodyText?: string;
}

/** First value of a header, case-insensitive. */
function header(lines: TrustInput["headerLines"], name: string): string {
    const want = name.toLowerCase();
    for (const h of lines) {
        if ((h.key || "").toLowerCase() !== want) continue;
        const colon = h.line.indexOf(":");
        return colon < 0 ? "" : h.line.slice(colon + 1).trim();
    }
    return "";
}

/** All values of a repeated header. */
function headerAll(lines: TrustInput["headerLines"], name: string): string[] {
    const want = name.toLowerCase();
    const out: string[] = [];
    for (const h of lines) {
        if ((h.key || "").toLowerCase() !== want) continue;
        const colon = h.line.indexOf(":");
        if (colon >= 0) out.push(h.line.slice(colon + 1).trim());
    }
    return out;
}

function bare(addr: string): string {
    const m = (addr || "").match(/[^\s<>,;]+@[^\s<>,;]+/);
    return (m ? m[0] : addr || "").toLowerCase().replace(/[.,;]+$/, "");
}

/**
 * What SpamAssassin's rules are evidence OF — which is not what its score is.
 *
 * The score answers "should this be delivered". The banner asks a different
 * question, "is this forged", and the `tests=` list that separates the two
 * sits in the same header and was never read. Four messages Bob subscribed to
 * (Constant Contact, Adobe MAX, Mokin, ActBlue) scored 2.2 to 10.9 and two of
 * them raised the app's strongest banner; every rule that pushed them there
 * was a bulk-mail test or a third-party blocklist entry about a host the
 * message merely LINKS TO. Mokin's 6.0 — more than the whole 4.5 threshold on
 * its own — was `URIBL_SBL` on the IP of the nameserver of a linked domain:
 * fourth-order guilt by association, saying nothing whatever about whether
 * Mokin sent the mail. All four passed DMARC aligned, so on all four the
 * headline "This message is not what it says it is" was factually false.
 * Full diagnosis: docs/spam-false-positives.md. (Claude Code 2026-08-27)
 *
 * Note what is NOT here: any tuning of a threshold. A rule's class follows
 * from what it tests, never from how much it scored.
 */
type RuleClass = "forgery" | "association" | "bulk" | "neutral" | "other";

/** Rules that say THIS message lies about who sent it. The only class that
 *  earns the red banner. `KAM_DMARC_STATUS` is deliberately absent — it is a
 *  0.0-scoring status marker that fired on Mokin, a message the receiving
 *  server had just recorded as `dmarc=pass`. */
const FORGERY_RULES: RegExp[] = [
    /^SPF_(HELO_)?(FAIL|SOFTFAIL)$/,
    /^(KAM_)?DMARC_(FAIL|REJECT|QUAR)/,
    /^DKIM_ADSP_(ALL|DISCARD|NXDOMAIN)$/,
    /^FORGED_/,
    /SPOOF/,
    /_PHISH/, /PHISHING/, /_MALW/, /MALWARE/,
];

/** Reputation a third party holds about some host the message mentions or
 *  travelled through. Real evidence the mail is unwanted; no evidence at all
 *  that the From line is false — the listed party is usually not the sender. */
const ASSOCIATION_RULES: RegExp[] = [
    /^URIBL_(SBL|BLACK|RED|DBL|ABUSE)/,
    /^SH_DBL/,
    /^SEM_URIBL/,
    /^SPAMHAUS_/,
    /^RCVD_IN_(SBL|XBL|PBL|BL_SPAMCOP|SORBS|BRBL|VALIDITY|MSPIKE_[LZ])/,
    // Blocklist rules that happen to live under other prefixes. KAM_*URIBL_*
    // ("Body contains URI listed in PCCC WILD RBL", 9.0) and SH_HBL_EMAILS
    // ("Email address listed in email blocklist", 8.0) are the same kind of
    // evidence as URIBL_SBL — somebody else's list, about a host or address
    // the message mentions — and they were the bulk of what still cautioned a
    // proved sender: a Business Insider newsletter, an MIT cancer-center
    // invitation, a campaign mailing. KAM_INFOUSMEBIZ judges the TLD a domain
    // lives in, which is guilt by neighbourhood.
    /^KAM_(BODY_|FROM_)?URIBL/, /^SH_HBL/, /^KAM_INFOUSMEBIZ$/,
];

/** The shape of mass mailing: bulk-detection networks, marketing markup, list
 *  plumbing. A score built entirely from these is a bulk verdict however high
 *  it climbs — Adobe MAX reached 3.9 without one rule that looks at identity. */
const BULK_RULES: RegExp[] = [
    /^DCC_/, /^PYZOR_/, /^RAZOR2_/,
    /^MAILING_LIST/, /^LIST_/,
    // KAM_* by name only, never the whole family: it holds marketing and list
    // rules alongside scam-CONTENT rules (KAM_BENEFICIARY is worth 10.0,
    // KAM_FAKE_NORTONLOW 6.5), and calling those bulk would quiet the loudest
    // true positives in the store.
    /^KAM_(BODY_)?MARKETINGBL/, /^KAM_UNSUB/, /^KAM_TRACKIMAGE$/,
    /^KAM_REALLYHUGEIMGSRC$/, /^KAM_DMARC_STATUS$/, /^KAM_COUPON/,
    /^HTML_/, /^MIME_HTML/, /^WORD_INVIS$/,
    /^URIBL_(GREY|CSS|BLOCKED|CT_SURBL)/, /^SH_BODYURI/,
    /^HEADER_FROM_DIFFERENT_DOMAINS$/,          // every ESP on earth trips this
    /^MPART_ALT_DIFF/, /^TVD_/, /^T_REMOTE_IMAGE$/, /^UNPARSEABLE_RELAY$/,
    // Branded ESP click-trackers. Not open redirectors (URI_PHP_REDIR and
    // friends stay unclassified): these are the sending platform's own
    // tracking domain, which is mass-mail machinery by definition.
    /^GB_AWSTRACK/, /AWSTRACK/, /^GB_SENDGRID/, /^GB_MAILCHIMP/,
];

/** Passes, whitelistings, and Bayes' own opinion — informational, and in
 *  BAYES_00's case exculpatory. `DKIM_INVALID` lives here rather than in the
 *  forgery class because a broken signature is routine on relayed and
 *  list-rewritten mail (Mokin, sent via Shopify and SendGrid, carries it while
 *  the receiving server records dkim=pass). It is promoted to forgery only
 *  when DMARC also failed — see classifyRule. */
const NEUTRAL_RULES: RegExp[] = [
    /^BAYES_/,
    /^SPF_(PASS|NONE|HELO_(PASS|NONE|NEUTRAL))$/,
    /^DKIM_(SIGNED|VALID|VALID_AU|VALID_EF|INVALID)$/,
    /^DKIMWL_/, /^RCVD_IN_(DNSWL|MSPIKE_H|IADB)/,
    /^SHORTCIRCUIT$/, /^AWL$/, /^NO_RELAYS$/, /^ALL_TRUSTED$/,
];

function classifyRule(name: string, dmarcFailed: boolean): RuleClass {
    if (dmarcFailed && name === "DKIM_INVALID") return "forgery";
    for (const re of FORGERY_RULES) if (re.test(name)) return "forgery";
    for (const re of ASSOCIATION_RULES) if (re.test(name)) return "association";
    for (const re of NEUTRAL_RULES) if (re.test(name)) return "neutral";
    for (const re of BULK_RULES) if (re.test(name)) return "bulk";
    return "other";
}

/**
 * Did the receiving server prove the From domain?
 *
 * Only the TOPMOST Authentication-Results is read. Headers are prepended, so
 * the first one is the one our own server wrote; every one below it travelled
 * with the message and a forger can write whatever they like there. Same
 * asymmetry serverSpamVerdict relies on, in the other direction: a claim that
 * SILENCES a warning must come from a source the sender cannot control.
 *
 * `dmarc=pass` is aligned by definition (RFC 7489) — SPF or DKIM passed on the
 * same domain the reader sees in From. The `header.from=` it names is checked
 * against the actual From line anyway, because an Authentication-Results about
 * some other message proves nothing about this one.
 */
function dmarcProof(input: TrustInput): { pass: boolean; failed: boolean; authFailures: string[]; domain: string; policy: string } {
    const auth = headerAll(input.headerLines, "authentication-results")[0] || "";
    if (!auth) return { pass: false, failed: false, authFailures: [], domain: "", policy: "" };

    // What the server caught failing, by name. An outright authentication
    // failure IS an identity test, whatever the tests= list happens to call
    // it — this is what keeps the 2026-08-25 phishes red under the new
    // classification, since spf=fail is the whole point of them.
    const authFailures = [
        /\bspf=(fail|softfail)\b/i.test(auth) ? "SPF" : "",
        /\bdkim=(fail|permerror)\b/i.test(auth) ? "DKIM" : "",
        /\bdmarc=(fail|permerror)\b/i.test(auth) ? "DMARC" : "",
    ].filter(Boolean);
    const failed = authFailures.includes("DMARC");
    if (!/\bdmarc=pass\b/i.test(auth)) return { pass: false, failed, authFailures, domain: "", policy: "" };

    const stated = (auth.match(/header\.from=([^\s;,]+)/i)?.[1] || "").toLowerCase();
    const policy = (auth.match(/policy\.dmarc=([^\s;,]+)/i)?.[1] || "").toLowerCase();
    const fromDomain = (bare(input.fromAddress).split("@")[1] || "").toLowerCase();
    if (stated && fromDomain
        && stated !== fromDomain
        && !stated.endsWith("." + fromDomain)
        && !fromDomain.endsWith("." + stated)) {
        return { pass: false, failed, authFailures, domain: "", policy };
    }
    return { pass: true, failed: false, authFailures, domain: stated || fromDomain, policy };
}

/** One rule that fired, with its score and description when the server left a
 *  report. `X-Spam-Report` appears only on flagged mail, so score and
 *  description are often absent while the name always is not. */
interface SpamRule {
    name: string;
    score: number;
    description: string;
    cls: RuleClass;
}

/** Unfold a header value the way RFC 5322 folds it: delete the line break,
 *  KEEP the whitespace that continues the line. SpamAssassin breaks its tests=
 *  list right after a comma, so that continuation whitespace is all that
 *  separates two rule names from being glued into one — the list is de-spaced
 *  after it has been cut out, not here. X-Spam-Report is NOT unfolded: there
 *  the line structure is the record structure. */
function unfold(value: string): string {
    return value.replace(/\r?\n/g, "");
}

/** Per-rule scores and descriptions out of X-Spam-Report, which reads:
 *
 *      *  6.0 URIBL_SBL Contains an URL's NS IP listed in the Spamhaus SBL
 *      *      blocklist
 *      *      [URI: ns12.xincache.com/112.80.181.111]
 *
 *  The bracketed evidence lines are dropped: they name the third party, and
 *  the finding is about this message. */
function parseSpamReport(report: string): Map<string, { score: number; description: string }> {
    const out = new Map<string, { score: number; description: string }>();
    let current: { score: number; description: string } = null;
    for (const raw of report.split(/\r?\n/)) {
        const line = raw.replace(/^[ \t]*\*?[ \t]*/, "").trimEnd();
        if (!line) continue;
        const start = line.match(/^(-?\d+(?:\.\d+)?)\s+([A-Z0-9_]{3,})\s*(.*)$/);
        if (start) {
            current = { score: Number(start[1]), description: start[3].trim() };
            out.set(start[2], current);
            continue;
        }
        if (current && !line.startsWith("[")) current.description += " " + line;
    }
    for (const rule of out.values())
        rule.description = rule.description
            .replace(/\s+/g, " ")
            .replace(/^(BODY|RAW|HEADER|URI|RBL):\s*/i, "")
            .trim();
    return out;
}

/**
 * What the server said, taken apart: the rules that fired, what each one is
 * evidence of, and whether the From line was proved independently.
 *
 * One mechanism, two consumers — the banner finding below and spamScoreOf's
 * chip — so the banner and the chip can never disagree about what a message is.
 */
interface ServerSpamAnalysis {
    score: number;
    threshold: number;
    flagged: boolean;
    rules: SpamRule[];
    /** Worst class that actually scored, forgery > association > other > bulk. */
    kind: "forgery" | "association" | "bulk" | "unclassified";
    /** The From line is proved — see provedBy for how. */
    proved: boolean;
    /** Which evidence proved it: the receiving server's DMARC verdict, a valid
     *  signature from the author's own domain, or the fact that the message
     *  never crossed an untrusted host. */
    provedBy: "dmarc" | "dkim" | "trusted" | "";
    provedDomain: string;
    provedPolicy: string;
    /** The reader's own trained filter put this at 0-1% spam. */
    bayesHam: boolean;
    /** Authentication the receiving server saw fail, by name: "SPF", "DKIM",
     *  "DMARC". Any of these is an identity finding on its own. */
    authFailures: string[];
    /** Top scoring rules, worst first, named for a human to judge in a second. */
    reasons: string[];
}

function analyzeServerSpam(input: TrustInput): ServerSpamAnalysis {
    const status = unfold(header(input.headerLines, "x-spam-status"));
    const flag = header(input.headerLines, "x-spam-flag");
    if (!status && !flag) return null;

    const score = Number(status.match(/score=(-?[\d.]+)/)?.[1]);
    const threshold = Number(status.match(/required=(-?[\d.]+)/)?.[1]);
    const flagged = /^yes/i.test(status) || /^yes/i.test(flag);

    const dmarc = dmarcProof(input);
    const report = parseSpamReport(header(input.headerLines, "x-spam-report"));
    // Everything up to the first lowercase key=value — SpamAssassin's trailing
    // shortcircuit=/autolearn=/version= fields. Cutting on "not a rule name"
    // rather than on whitespace is what keeps "...,WORD_INVIS shortcircuit=no"
    // from delivering a rule called WORD_INVISshortcircuit.
    const names = (status.match(/tests=([\s\S]*?)(?=\s+[a-z_]+=|$)/)?.[1] || "")
        .replace(/\s+/g, "").split(",").map(n => n.toUpperCase()).filter(Boolean);

    const rules: SpamRule[] = names.map(name => {
        const found = report.get(name);
        return {
            name,
            score: found ? found.score : NaN,
            description: found ? found.description : "",
            cls: classifyRule(name, dmarc.failed),
        };
    });

    // A rule that scored zero or negative did not put this message over any
    // line, so it cannot be the reason the message is here. Unscored rules
    // (no X-Spam-Report, which is most unflagged mail) still count — absence
    // of a report is not evidence the rule was free.
    const scored = (r: SpamRule) => !(r.score <= 0);
    const present = (cls: RuleClass) => rules.some(r => r.cls === cls && scored(r));

    // An unknown rule keeps a proved sender at caution only if it could have
    // flagged the message BY ITSELF. Presence was too crude: an IEEE
    // conference announcement (Bob 2026-08-28) scored 4.5 of 4.5 on DCC 3.0 +
    // marketing 1.0 + 1.5 for GB_AWSTRACK_REDIR — Amazon SES's click-tracker,
    // which every bulk mailer on earth routes through — and the 1.5 alone
    // pushed a DMARC-proved announcement into "check this message before
    // acting on it". Meanwhile KAM_BENEFICIARY, the case this rule exists for,
    // is worth 10.0 on its own.
    //
    // The comparison is against the SERVER'S OWN threshold, so this adds no
    // constant to tune: "enough to condemn" already has a number, and it is
    // the one the server used. Unscored rules (no X-Spam-Report) make the sum
    // unknown, and unknown stays cautious.
    const unknown = rules.filter(r => r.cls === "other" && scored(r));
    const unknownWeight = unknown.reduce((sum, r) => sum + r.score, 0);
    const unknownCouldFlagAlone = !unknown.length ? false
        : !Number.isFinite(unknownWeight) || !Number.isFinite(threshold) ? true
        : unknownWeight >= threshold;
    const kind = (dmarc.authFailures.length || present("forgery")) ? "forgery"
        : present("association") ? "association"
        : unknownCouldFlagAlone ? "unclassified"
        : rules.length ? "bulk"
        : "unclassified";

    // Two proofs SpamAssassin states in its own rule list, which mail that
    // never crossed a trust boundary carries INSTEAD of an Authentication-Results
    // header — Bob's own message to John Levine about spam handling, 2026-08-27,
    // had neither AR nor DMARC and got a caution banner reading "links and
    // attachments here should not be opened" on prose he had just written:
    //
    //   ALL_TRUSTED    Passed through trusted hosts only via SMTP
    //   DKIM_VALID_AU  Message has a valid DKIM or DK signature from author's domain
    //
    // DKIM_VALID_AU is the same evidence DMARC alignment rests on — the author
    // domain signed it and the signature verified — so it proves the From line on
    // its own. ALL_TRUSTED says the message reached the mailbox without passing
    // through a host the server distrusts, which is Bob's own authenticated
    // submission; nothing could have injected it in transit. (What flagged his
    // message was URIBL_DBL_SPAM 8.0 on blueorchestra.org — the domain he was
    // quoting IN a discussion about spam. Guilt by association at its purest.)
    const has = (name: string) => rules.some(r => r.name === name);
    const provedBy = dmarc.pass ? "dmarc"
        : has("DKIM_VALID_AU") ? "dkim"
        : has("ALL_TRUSTED") ? "trusted"
        : "";
    const fromDomain = (bare(input.fromAddress).split("@")[1] || "").toLowerCase();

    const reasons = rules
        .filter(r => r.cls !== "neutral" && scored(r))
        .sort((a, b) => (b.score || 0) - (a.score || 0))
        .slice(0, 3)
        .map(r => {
            const num = Number.isFinite(r.score) ? `${r.score.toFixed(1)} ` : "";
            return r.description ? `${num}${r.name} (${r.description})` : `${num}${r.name}`;
        });

    return {
        score, threshold, flagged, rules, kind,
        proved: !!provedBy, provedBy,
        provedDomain: dmarc.domain || fromDomain, provedPolicy: dmarc.policy,
        bayesHam: rules.some(r => r.name === "BAYES_00" || r.name === "BAYES_01"),
        authFailures: dmarc.authFailures,
        reasons,
    };
}

/**
 * Did the receiving server's spam filter already say yes — and if so, to WHAT?
 *
 * The highest-value signal available, and it costs nothing: the mail server
 * ran SpamAssassin before delivery and wrote the answer into the message.
 * mailx was throwing it away.
 *
 * Only a POSITIVE verdict is trusted, never a negative one. Anyone can inject
 * `X-Spam-Flag: NO` into a message they send, and treating that as a clean
 * bill of health would hand attackers a switch for turning the check off. A
 * forged YES only flags the forger's own mail, so the asymmetry is safe.
 *
 * The severity comes from the RULES, never from the number (Claude Code
 * 2026-08-27):
 *   - a forgery rule fired          -> danger. This is what red is for.
 *   - the sender is DMARC-proved     -> info. The mail may well be unwanted,
 *     and every rule that scored is     but "not what it says it is" is a
 *     one this file can name, or        claim its own evidence contradicts.
 *     the whole score is bulk rules
 *   - anything else                  -> caution. Flagged, and the reason is
 *                                      not identity — or is a rule this file
 *                                      cannot classify, which is never a
 *                                      reason to go quiet.
 */
/** How the From line was proved, in the reader's terms — named so it can be
 *  checked against the headers rather than believed. */
function provedNote(a: ServerSpamAnalysis): string {
    switch (a.provedBy) {
        case "dmarc":
            return `DMARC pass, aligned${a.provedPolicy ? `, policy ${a.provedPolicy}` : ""}.`;
        case "dkim":
            return `Valid signature from ${a.provedDomain}, the sending domain itself (DKIM_VALID_AU).`;
        case "trusted":
            return "It passed through trusted hosts only (ALL_TRUSTED) — nothing untrusted handled it.";
        default:
            return "";
    }
}

function serverSpamVerdict(input: TrustInput): TrustFinding {
    const a = analyzeServerSpam(input);
    if (!a || !a.flagged) return null;

    // Mail from a sender the reader has approved, whose identity is proved,
    // and which nothing accuses of forgery: say nothing at all. The server's
    // score answers "is this bulk", the reader has already answered "is it
    // wanted", and repeating the first after the second is how a banner turns
    // into furniture. (Bob 2026-08-28, having just clicked "Always:
    // *@icecer.com" on a conference announcement: "I accepted the server so
    // shouldn't this be considered safe?")
    const trustedAndProved = input.senderTrusted && a.proved;
    if (trustedAndProved && a.kind !== "forgery" && !a.authFailures.length) return null;

    const numbers = Number.isFinite(a.score) && Number.isFinite(a.threshold)
        ? `SpamAssassin score ${a.score} of ${a.threshold}`
        : "flagged by SpamAssassin";
    const why = a.reasons.length ? ` — ${a.reasons.join("; ")}` : "";
    const bayes = a.bayesHam ? "; your own trained filter puts it at 0-1% spam (BAYES_00)" : "";

    if (a.kind === "forgery")
        return {
            id: "server-spam-verdict",
            severity: "danger",
            text: "Your mail server classified this as spam, and the rules that fired test who sent it.",
            detail: a.authFailures.length
                ? `${a.authFailures.join(" and ")} authentication failed at your server. ${numbers}${why}`
                : `${numbers}${why}`,
        };

    if (a.proved && a.kind !== "unclassified")
        return {
            id: "server-spam-verdict",
            severity: "info",
            text: a.provedBy === "trusted"
                ? "Your mail server scored this as spam, but it reached you without leaving your own systems."
                : `Your mail server scored this as spam, but ${a.provedDomain} proved it sent this.`,
            detail: `${provedNote(a)} ${numbers}${why}${bayes}`,
        };

    if (a.kind === "bulk")
        return {
            id: "server-spam-verdict",
            severity: "info",
            text: "Your mail server rates this bulk mail — nothing that scored tests who sent it.",
            detail: `${numbers}${why}${bayes}`,
        };

    const proof = a.proved ? `${provedNote(a)} ` : "";
    if (input.senderTrusted && !a.proved)
        return {
            id: "server-spam-verdict",
            severity: "caution",
            text: "This claims to be a sender you trust, but nothing proves it is.",
            detail: `No DMARC pass, no valid signature from the sending domain, and it passed through hosts your server does not trust. ${numbers}${why}`,
        };
    return {
        id: "server-spam-verdict",
        severity: "caution",
        text: "Your mail server classified this as spam before delivering it.",
        detail: `${proof}${numbers}${why}${bayes}`,
    };
}

/**
 * Does the message claim to come from the reader themselves, while failing the
 * authentication that would prove it?
 *
 * Either half alone is ordinary — people really do mail themselves notes, and
 * forwarded mail really does fail SPF. Together they are not: mail genuinely
 * from your own account authenticates as your own account. This is the most
 * common shape of targeted phish, because a From line showing your own address
 * defeats every visual check a reader makes.
 *
 * Requires an Authentication-Results header to exist. A message without one
 * never crossed a trust boundary — it is your own mail, handled internally —
 * and is not evidence of anything. That case is the overwhelming majority:
 * 10,034 of the 10,338 self-claimed messages in a 50,000-message scan of Bob's
 * store had no such header at all.
 *
 * `spf=none` counts as "not authenticated", not just `fail`. A domain with no
 * SPF record published can never produce a failure, only a shrug — so treating
 * `none` as neutral leaves the least-protected domain the least defended.
 * bobf.frankston.com publishes no SPF, and that is precisely the hole the
 * 2026-08-25 sextortion mail walked through: SpamAssassin scored it 2.7
 * against a 4.5 threshold and delivered it. In that same 50,000-message scan
 * `spf=none` on self-claimed mail occurred 18 times and was spam nearly every
 * time ("Settle your debt", "AAA Final notice", "McAfee Protection Plan
 * Ended"); legitimate self-mail was in the no-header group, untouched by this.
 */
function selfSpoof(input: TrustInput): TrustFinding {
    const from = bare(input.fromAddress);
    if (!from) return null;
    const own = new Set(input.ownAddresses.map(bare).filter(Boolean));
    if (!own.has(from)) return null;

    const auth = headerAll(input.headerLines, "authentication-results").join(" ; ");
    if (!auth) return null;
    const failed = [
        /\bspf=(fail|softfail)\b/i.test(auth) ? "SPF" : "",
        /\bdkim=(fail|permerror)\b/i.test(auth) ? "DKIM" : "",
        /\bdmarc=(fail|permerror)\b/i.test(auth) ? "DMARC" : "",
    ].filter(Boolean);
    // Nothing failed, but did anything actually PASS? "spf=none dkim=none" is
    // not a clean bill of health for a message claiming to be you.
    if (!failed.length) {
        if (/\b(spf|dkim|dmarc)=pass\b/i.test(auth)) return null;
        const envelopeAddr = bare(header(input.headerLines, "return-path"));
        const note = envelopeAddr && envelopeAddr !== from ? `; envelope sender ${envelopeAddr}` : "";
        return {
            id: "self-spoof",
            severity: "danger",
            text: `This claims to be from your own address, ${from}, and nothing proves it came from your account.`,
            detail: `no SPF, DKIM or DMARC result to check against${note} — your domain publishes no SPF record, so forgery cannot be detected by the sending side`,
        };
    }

    // Name the envelope sender when it disagrees — that is the address that
    // actually sent this, and seeing it beside the claim is the whole story.
    const envelope = bare(header(input.headerLines, "return-path"));
    const envelopeNote = envelope && envelope !== from ? `; actually sent by ${envelope}` : "";
    return {
        id: "self-spoof",
        severity: "danger",
        text: `This claims to be from your own address, ${from}, but it did not come from your account.`,
        detail: `${failed.join(" and ")} authentication failed${envelopeNote}`,
    };
}

/** Bigger than any real image. An honest layout never needs a hotspot this
 *  large; a forger picks an absurd number so they don't have to know the
 *  rendered size. Not a tuned threshold — a sanity bound. */
const OVERLAY_MIN_EDGE_PX = 2000;

/**
 * Is an entire image covered by one invisible link?
 *
 * `<area coords="0,0,10000,10000">` turns every pixel of an image into a link
 * with no underlined text, no visible URL, and no safe area to click. Real
 * mail uses image maps almost never, and never at that size.
 */
function hiddenLinkOverlay(bodyHtml: string): TrustFinding {
    for (const m of bodyHtml.matchAll(/<area\b[^>]*>/gi)) {
        const tag = m[0];
        const href = tag.match(/href\s*=\s*["']([^"']+)["']/i)?.[1];
        if (!href) continue;
        const coords = (tag.match(/coords\s*=\s*["']([^"']+)["']/i)?.[1] || "")
            .split(/[,\s]+/).map(Number).filter(n => Number.isFinite(n));
        if (coords.length < 4) continue;
        const width = Math.abs(coords[2] - coords[0]);
        const height = Math.abs(coords[3] - coords[1]);
        if (width < OVERLAY_MIN_EDGE_PX || height < OVERLAY_MIN_EDGE_PX) continue;
        let host = href;
        try { host = new URL(href).hostname; } catch { /* keep the raw href */ }
        return {
            id: "hidden-link-overlay",
            severity: "danger",
            text: "The picture in this message is one large hidden link — clicking anywhere on it goes to the same place.",
            detail: `${width}x${height} clickable area over the image, pointing at ${host}`,
        };
    }
    return null;
}

/**
 * Does a link route through a redirector carrying its real destination in the
 * query string?
 *
 * `castanet-sa.fr/modules/babel/redirect.php?newurl=https%3A%2F%2Fevil.example`
 * borrows a real site's reputation: the visible host is a legitimate business
 * whose redirect script was left open, and the destination appears only
 * percent-encoded inside a parameter. Reporting the ENDPOINT is the point — a
 * reader cannot be expected to URL-decode a query string.
 */
function redirectorLink(bodyHtml: string): TrustFinding {
    for (const m of bodyHtml.matchAll(/href\s*=\s*["']([^"']+)["']/gi)) {
        let url: URL;
        try { url = new URL(m[1]); } catch { continue; }
        if (!/^https?:$/.test(url.protocol)) continue;
        for (const [, value] of url.searchParams) {
            let target: URL;
            try { target = new URL(value); } catch { continue; }
            if (!/^https?:$/.test(target.protocol)) continue;
            if (target.hostname === url.hostname) continue;   // same-site, not laundering
            return {
                id: "redirector-link",
                severity: "caution",
                text: `A link hides where it goes: it passes through ${url.hostname} and ends at ${target.hostname}.`,
                detail: `${url.hostname} -> ${target.hostname}`,
            };
        }
    }
    return null;
}

/**
 * Everything findable about this message, worst first.
 *
 * Returns an empty array for ordinary mail — the caller then shows nothing,
 * rather than a reassuring "looks fine" this cannot support. Absence of
 * evidence is not a clean bill of health.
 */
export function assessMessageTrust(input: TrustInput): TrustFinding[] {
    const findings = [
        serverSpamVerdict(input),
        selfSpoof(input),
        relayAuthMismatch(input),
        zeroWidthObfuscation(input.bodyText || ""),
        hiddenLinkOverlay(input.bodyHtml || ""),
        redirectorLink(input.bodyHtml || ""),
    ].filter(Boolean);
    const rank = { danger: 0, caution: 1, info: 2 };
    return findings.sort((a, b) => rank[a.severity] - rank[b.severity]);
}

/**
 * Did the relay record a different account than the one the message claims?
 *
 * Shared hosts stamp the identity that actually authenticated to submit the
 * message. That stamp is added by the RELAY, downstream of whoever sent it, so
 * a forger cannot remove or edit it — which makes disagreement between the
 * stamp and the From line about as close to proof as mail headers get.
 *
 * The 2026-08-25 sextortion mail (Bob: "note that the origin is different than
 * the from domain in antiabuse and in source-auth"):
 *
 *   From:            universe@bobf.frankston.com
 *   X-Source-Auth:   admin@calebjross.com
 *   X-Source-Cap:    (base64) calebjro;calebjro;box2202.bluehost.com
 *   Message-ID:      <...@calebjross.com>
 *
 * A Bluehost cPanel account belonging to an unrelated domain authenticated and
 * sent mail wearing Bob's address. SpamAssassin scored the whole thing 2.7
 * against a 4.5 threshold and let it through.
 *
 * Compared at the registrable-domain level, not the full address: a host that
 * legitimately sends for a domain authenticates as some account AT that
 * domain, and the local part varies for perfectly ordinary reasons.
 */
const RELAY_IDENTITY_HEADERS = [
    "x-source-auth",           // cPanel / Exim (Bluehost, HostGator, most shared hosts)
    "x-authenticated-sender",  // Postfix / Communigate
    "x-auth-id",
    "x-authenticated-user",
];

function relayAuthMismatch(input: TrustInput): TrustFinding {
    const from = bare(input.fromAddress);
    const fromDomain = from.split("@")[1] || "";
    if (!fromDomain) return null;

    for (const name of RELAY_IDENTITY_HEADERS) {
        const stamped = bare(header(input.headerLines, name));
        const stampedDomain = stamped.split("@")[1] || "";
        if (!stampedDomain || stampedDomain === fromDomain) continue;
        // Sub/parent-domain is the same organisation — mail.example.com
        // sending for example.com is ordinary. Cross-organisation is not.
        if (stampedDomain.endsWith("." + fromDomain) || fromDomain.endsWith("." + stampedDomain)) continue;
        return {
            id: "relay-auth-mismatch",
            severity: "danger",
            text: `The account that actually sent this belongs to someone else: ${stampedDomain}, not ${fromDomain}.`,
            detail: `the sending server recorded ${stamped} as the authenticated sender, while the message claims to be from ${from}`,
        };
    }
    return null;
}

/**
 * Is the text stuffed with invisible characters to break word matching?
 *
 * Zero-width space, zero-width non-joiner, zero-width joiner and the
 * zero-width no-break space render as nothing, so a reader sees plain prose
 * while every filter matching on words sees gibberish. The 2026-08-25
 * sextortion mail put one between nearly every pair of letters — "i regret to
 * inform you" reached the parser as `i re‍g﻿ret t‌o ‌in​f‌or﻿m​ you`. SpamAssassin
 * spotted it (UNICODE_OBFU_ZW_MANY) and still scored the message 2.7 against a
 * 4.5 threshold, so it was delivered.
 *
 * Counted only BETWEEN TWO LATIN LETTERS, which is what obfuscation looks like
 * and what the legitimate uses do not: an emoji ZWJ sequence joins pictographs,
 * Arabic and Indic ZWNJ sit between their own scripts' letters, and a soft
 * hyphen marks a break opportunity rather than splitting a word mid-render.
 *
 * The count guards against a single stray character surviving a copy-paste out
 * of a web page — it is a sanity bound, not a tuned dial. Prose does not
 * accumulate dozens of these by accident; the sample message had over 900.
 */
const ZERO_WIDTH_MIN_OCCURRENCES = 12;

function zeroWidthObfuscation(bodyText: string): TrustFinding {
    if (!bodyText) return null;
    const between = bodyText.match(/[A-Za-z][​‌‍﻿][A-Za-z]/g);
    const count = between ? between.length : 0;
    if (count < ZERO_WIDTH_MIN_OCCURRENCES) return null;
    return {
        id: "zero-width-obfuscation",
        severity: "danger",
        text: "The words in this message are stuffed with invisible characters to get past spam filters.",
        detail: `${count} zero-width characters hidden inside words — legitimate mail has no reason to do this`,
    };
}

/**
 * The receiving server's spam score, whether or not it crossed the threshold.
 *
 * Separate from the findings on purpose. A finding is an accusation and only
 * fires on evidence; this is a measurement, and the reader asked to see the
 * number itself (Bob 2026-08-25: "should we have a flag ... with the spam
 * assassin number and a color code?"). The sextortion mail is exactly why:
 * 2.7 against a 4.5 threshold is not "clean", it is "did not quite trip the
 * wire", and those are very different things to a person deciding whether to
 * trust a message.
 *
 * Returns null when the server left no score — most mail from providers that
 * filter server-side and say nothing in the headers.
 */
export interface SpamScore {
    score: number;
    threshold: number;
    /** The server's own yes/no, which is simply score >= threshold. */
    flagged: boolean;
    /** What the rules that actually scored are evidence OF. `bulk` and a
     *  `proved` sender are the two ways a high number can be honest mail —
     *  the chip stays visible and says so, rather than going red at a number
     *  and teaching the reader to ignore it. (Claude Code 2026-08-27) */
    kind: "forgery" | "association" | "bulk" | "unclassified";
    /** The From line is proved — provedBy says by what. */
    proved: boolean;
    /** Which evidence: the server's DMARC verdict, a valid signature from the
     *  author's own domain, or never having crossed an untrusted host. The
     *  chip's tooltip has to say which one, or it asserts a DMARC pass that
     *  may not exist (Bob's own mail carries no Authentication-Results at
     *  all). */
    provedBy: "dmarc" | "dkim" | "trusted" | "";
    /** Top scoring rules, named — "6.0 URIBL_SBL (Contains an URL's NS IP
     *  listed in the Spamhaus SBL blocklist)". A number alone is unactionable;
     *  this can be judged in a second. */
    reasons: string[];
    /** The reader approved this sender AND the sender is proved: the score is
     *  still true, and no longer worth showing. The viewer hides the chip. */
    trusted: boolean;
}

export function spamScoreOf(input: TrustInput): SpamScore {
    const a = analyzeServerSpam(input);
    if (!a || !Number.isFinite(a.score) || !Number.isFinite(a.threshold)) return null;
    return {
        score: a.score,
        threshold: a.threshold,
        flagged: a.flagged,
        kind: a.kind,
        proved: a.proved,
        provedBy: a.provedBy,
        trusted: !!input.senderTrusted && a.proved && a.kind !== "forgery" && !a.authFailures.length,
        reasons: a.reasons,
    };
}
